import React, { useContext, useMemo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Redirect, useHistory } from 'react-router-dom';
import { message, Page, Spin } from 'components';
import { PageProps } from 'components/Page';
import { AuthContext, SetupBrokerageWizardModalContext } from 'contexts';
import { Color } from '@bb/elements-web';
import {
  checkUserBillingPermission,
  featureEnabled,
  featureFlagClient,
  hasValidStripeMlsProductId,
} from 'utils';
import { sendPaymentUpdatedNotification } from 'api/NotificationController';
import { getOnboardingPortalLaunchDatesAssociations } from 'api/OnboardingPortalController';
import { SHOW_UPDATE_CARD_EVENT } from '../../components/ManageCard/Content';
import { PortalAssociation } from '../../onboarding/types';
import {
  BalanceAlert,
  BalanceCard,
  DocumentsCard,
  ManageCard,
  InvoicesCard,
  SubscriptionsCard,
} from './components';
import BillingInvoicesPageContextProvider, {
  BillingInvoicesPageContext,
  type CanManageType,
  type OverdueBalanceType,
} from './BillingInvoicesPageContext';
import BillingDetailsCard from './components/BillingDetailsCard';
import BillingProfileErrorStatus from './components/BillingProfileErrorStatus';
import { isOfTypeTabKey } from '../UserViewPage/types';
import { useSearchParams } from '../ShowingsCalendarPage/hooks';

type BillingInvoicesPageProps = {
  userId?: string;
  userMlsNumbers?: { boardId?: string }[];
};

// Create an outer page that we export so we can pass along the BillingInvoicesPageContextProvider
const BillingInvoicesPage: React.FC<BillingInvoicesPageProps> = props => {
  const { auth } = useContext(AuthContext);
  return (
    <BillingInvoicesPageContextProvider
      orgId={auth.org.id}
      billingCustomerNumber={auth.billingCustomerNumber}
    >
      <BillingInvoicesPageInnerContent {...props} />
    </BillingInvoicesPageContextProvider>
  );
};

// Create an inner page separately from the outer page so that we can receive the BillingInvoicesPage Context
const BillingInvoicesPageInnerContent: React.FC<BillingInvoicesPageProps> = ({
  userId,
  userMlsNumbers,
}) => {
  const { auth, fetchAuth, answeringPortalConfig } = useContext(AuthContext);
  const {
    loadingOverdueFees,
    proratedPricing,
    reinstatementFee,
    currencyCode,
  } = useContext(BillingInvoicesPageContext);

  const { closeModal } = useContext(SetupBrokerageWizardModalContext);
  const [association, setAssociation] = useState<PortalAssociation>();
  const history = useHistory();
  const productType = userId ? 'appointmentDesk' : 'orgBilling';
  const isMlsCovered = answeringPortalConfig?.appointmentDeskMlsPay;

  const isBoardCovered = useMemo(() => {
    // board only can cover users atm
    if (productType === 'orgBilling') return false;
    return answeringPortalConfig?.appointmentDeskBoardPays
      ?.filter(({ payForUsers }) => payForUsers)
      .some(({ boardId }) =>
        userMlsNumbers?.some(
          ({ boardId: userBoardId }) => boardId === userBoardId,
        ),
      );
  }, [
    answeringPortalConfig?.appointmentDeskBoardPays,
    productType,
    userMlsNumbers,
  ]);

  // Parse tab param
  const { getSearchParam, deleteSearchParam } = useSearchParams();
  useEffect(() => {
    const parseSearchParams = () => {
      const activeTab = getSearchParam('tab') as string;
      if (activeTab && isOfTypeTabKey(activeTab) && activeTab === 'BILLING') {
        deleteSearchParam('tab');
      }
      const payNow = (getSearchParam('payNow') as string) === 'true';
      if (payNow) {
        document.dispatchEvent(new Event(SHOW_UPDATE_CARD_EVENT(userId)));
        deleteSearchParam('payNow');
      }
    };
    parseSearchParams();
  }, [deleteSearchParam, getSearchParam, userId]);

  useEffect(() => {
    const fetchAssociations = async () => {
      const { onboardingPortalId } = auth.org;
      if (!onboardingPortalId) return [];

      const portalData = await getOnboardingPortalLaunchDatesAssociations(
        onboardingPortalId,
        auth.org.id,
      );
      return portalData?.associations || [];
    };
    const setupAssociation = async () => {
      const associations = await fetchAssociations();

      if (associations.length) {
        if (userId) {
          // FIXME: user mls might not match auth if managing for another user
          const userPrimaryAssociationId = auth.mls_numbers.find(
            mls => mls.isPrimary,
          )?.boardId;
          const onboardingAssociation = associations.find(
            a => a.id === userPrimaryAssociationId,
          );
          if (onboardingAssociation) setAssociation(onboardingAssociation);
        } else {
          const onboardingAssociation =
            associations.find(a => a.useLiveConciergePlus) ||
            associations.find(a => a.useAppointmentDesk) ||
            associations[0];
          if (onboardingAssociation) setAssociation(onboardingAssociation);
        }
      }
    };
    setupAssociation();
  }, [auth, userId]);

  const isAssumedUser = useMemo(
    () =>
      auth.assumingUserId !== undefined ||
      auth.isAssumedNotBB ||
      auth.assumerIdMlsStaff !== undefined,
    [auth],
  );

  const isBillingUser = useMemo(
    () => !userId || userId === auth.id,
    [userId, auth.id],
  );

  // Pass this data in for if the user can manage billing
  const canManageBilling = useMemo<CanManageType>(() => {
    if (isAssumedUser) {
      return {
        response: false,
        message: 'You cannot manage billing details while assuming a user.',
      };
    }

    if (!isBillingUser) {
      return {
        response: false,
        message: 'Only the owner of this account may update payment method.',
      };
    }

    return {
      response: true,
    };
  }, [isAssumedUser, isBillingUser]);

  // Pass this data in for if the user has an overdue balance
  const isSupraSubOverdue = useMemo<OverdueBalanceType>(() => {
    if (
      featureEnabled('keyholderBillingFeature') &&
      featureEnabled('supraBillingApiFeature')
    ) {
      // Show the price breakdown of what they owe if they are In-Collections
      if (auth.supraSubscription?.status === 'deactivated') {
        const priceBreakdown = (
          <OverduePriceBreakdown>
            <PriceBreakdownLineItem>
              <span>Prorated pricing</span>
              <span>${proratedPricing}</span>
            </PriceBreakdownLineItem>
            <PriceBreakdownLineItem>
              <span>Reinstatement fee</span>
              <span>${reinstatementFee}</span>
            </PriceBreakdownLineItem>
            <PriceSeparator />
            <PriceBreakdownLineItem>
              <div>
                <span>Total</span>
                <TaxesNote> (+applicable taxes)</TaxesNote>
              </div>
              <span>
                ${proratedPricing + reinstatementFee} {currencyCode}
              </span>
            </PriceBreakdownLineItem>
          </OverduePriceBreakdown>
        );
        return {
          response: true,
          message: (
            <>
              <div>
                You will need to update your credit card information and the
                outstanding balance will be automatically charged to the new
                card.
              </div>
              &nbsp;
              <OutstandingBalanceHeader>
                Outstanding balance
              </OutstandingBalanceHeader>
              {/* Three options: show a loading symbol, show the price breakdown, or show an error */}
              {loadingOverdueFees === 'loading' && (
                <CenteredDiv>
                  <Spin size="large" />
                </CenteredDiv>
              )}
              {loadingOverdueFees === 'done' && priceBreakdown}
              {loadingOverdueFees === 'error' && (
                <div>
                  An error occurred while fetching the required payment amount.
                  Please contact the Supra customer success team.
                </div>
              )}
            </>
          ),
        };
      }

      // Show just a confirmation acknowledging they will be charged if they are only slightly overdue
      if (auth.supraSubscription?.status === 'suspended') {
        return {
          response: true,
          message: (
            <p>
              You will need to update your credit card information and the
              outstanding balance will be automatically charged to the new card.
            </p>
          ),
        };
      }
    }

    return {
      response: false,
    };
  }, [
    auth.supraSubscription?.status,
    loadingOverdueFees,
    proratedPricing,
    reinstatementFee,
    currencyCode,
  ]);

  const canManageSubscription = useMemo<CanManageType>(() => {
    if (productType === 'orgBilling' && isAssumedUser) {
      return {
        response: false,
        message: 'You cannot manage subscriptions while assuming a user.',
      };
    }

    return {
      response: true,
      message: '',
    };
  }, [isAssumedUser, productType]);

  if (productType === 'orgBilling' && !checkUserBillingPermission(auth)) {
    message.error(
      'You do not have permissions to view this page. Please contact support@brokerbay.com for more information.',
    );
    return <Redirect to="/" />;
  }

  const pageProps: PageProps = {
    title: 'Billing',
    type: 'hollow',
    fillScreenHeight: true,
    showBackButton: true,
  };

  if (auth?.isAssumedNotBB) return null;

  const redirectToMain = async () => {
    closeModal();
    await fetchAuth();
    history.push('/');
  };

  const pageContent = (
    <StyledWrapper>
      <BalanceAlert
        orgId={auth.org.id}
        userId={userId || ''}
        type={userId ? 'user' : 'org'}
      />
      <BillingProfileErrorStatus />
      {
        // Display the user balance of someone who is utilizing keyholder billing
        featureEnabled('keyholderBillingFeature') &&
        auth.billingCustomerNumber ? (
          <BalanceCard
            billingCustomerNumber={auth.billingCustomerNumber}
            type={'keyholderBillingUser'}
          />
        ) : (
          // Display the user/org balance of someone who is not utilizing keyholder billing
          <BalanceCard
            orgId={auth.org.id}
            userId={userId || ''}
            type={userId ? 'legacyUser' : 'org'}
          />
        )
      }
      <ManageCard
        userId={userId}
        productType={productType}
        stripePk={process.env.STRIPE_PK!}
        canManageBilling={canManageBilling}
        isSupraSubOverdue={isSupraSubOverdue}
        onCardUpdateSuccess={() => {
          if (productType === 'orgBilling' && auth?.role !== 'OWNER')
            sendPaymentUpdatedNotification();
        }}
      />
      {featureFlagClient.featureEnabled('keyholderBillingFeature') &&
        auth.billingCustomerNumber && (
          <BillingDetailsCard canManageBilling={canManageBilling} />
        )}
      <SubscriptionsCard
        userId={userId}
        redirectToMain={redirectToMain}
        isMlsCovered={isMlsCovered}
        isBoardCovered={isBoardCovered}
        canManageSubscription={canManageSubscription}
        useLiveConciergePlus={association?.useLiveConciergePlus}
        hasValidStripeMlsProductId={hasValidStripeMlsProductId(
          answeringPortalConfig?.mlsProductId,
        )}
        conciergeEnabled={association?.conciergeEnabled}
      />
      <InvoicesCard orgId={auth.org.id} userId={userId} />
      <DocumentsCard userBillingNumber={auth.billingCustomerNumber} />
    </StyledWrapper>
  );

  return userId ? pageContent : <Page {...pageProps}>{pageContent}</Page>;
};

const StyledWrapper = styled.div`
  display: grid;
  gap: 25px;
`;

const OverduePriceBreakdown = styled.div`
  background-color: ${Color.LIGHTER_GREY};
  padding: 16px;
  width: 100%;
  border-radius: 4px;
`;
const OutstandingBalanceHeader = styled.h2`
  font-weight: 600;
  font-size: 18px;
`;

const PriceBreakdownLineItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
`;
const TaxesNote = styled.span`
  color: #585e72;
`;

const PriceSeparator = styled.hr`
  border: none;
  border-top: 1px solid #ccc;
  margin: 12px 0;
`;

const CenteredDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default BillingInvoicesPage;
